import { Row, Table, Col } from "reactstrap"
import { Link } from "react-router-dom"
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table"
import React from "react"

// Separate Pagination Component
const TablePagination = ({
  paginationInfo,
  onPageChange,
  paginationWrapper = "dataTables_paginate paging_simple_numbers pagination-rounded",
  pagination = "pagination",
}) => {
  if (!paginationInfo || paginationInfo.last_page <= 1) return null

  const { current_page, last_page, from, to, total } = paginationInfo

  // Function to generate page numbers with ellipsis
  const generatePageNumbers = () => {
    const delta = 2 // Number of pages to show on each side of current page
    const pages = []
    const left = current_page - delta
    const right = current_page + delta

    // Always include first page
    pages.push(1)

    // Add ellipsis after first page if needed
    if (left > 2) {
      pages.push("...")
    }

    // Add pages around current page
    for (let i = Math.max(2, left); i <= Math.min(last_page - 1, right); i++) {
      pages.push(i)
    }

    // Add ellipsis before last page if needed
    if (right < last_page - 1) {
      pages.push("...")
    }

    // Always include last page if there's more than one page
    if (last_page > 1) {
      pages.push(last_page)
    }

    return pages
  }

  const pageNumbers = generatePageNumbers()

  return (
    <Row className="align-items-center mt-3">
      <Col sm={12} md={5}>
        <div className="dataTables_info">
          Showing {from} to {to} of {total} Results
        </div>
      </Col>
      <Col sm={12} md={7}>
        <div className={paginationWrapper}>
          <ul className={pagination}>
            {/* Previous Button */}
            <li
              className={`paginate_button page-item previous ${
                current_page <= 1 ? "disabled" : ""
              }`}
            >
              <Link
                to="#"
                className="page-link"
                onClick={e => {
                  e.preventDefault()
                  if (current_page > 1) {
                    onPageChange(current_page - 1)
                  }
                }}
              >
                <i className="mdi mdi-chevron-left"></i>
              </Link>
            </li>

            {/* Page Number Buttons */}
            {pageNumbers.map((page, index) => (
              <li
                key={index}
                className={`paginate_button page-item ${
                  page === current_page ? "active" : ""
                } ${page === "..." ? "disabled" : ""}`}
              >
                {page === "..." ? (
                  <span className="page-link">...</span>
                ) : (
                  <Link
                    to="#"
                    className="page-link"
                    onClick={e => {
                      e.preventDefault()
                      onPageChange(page)
                    }}
                  >
                    {page}
                  </Link>
                )}
              </li>
            ))}

            {/* Next Button */}
            <li
              className={`paginate_button page-item next ${
                current_page >= last_page ? "disabled" : ""
              }`}
            >
              <Link
                to="#"
                className="page-link"
                onClick={e => {
                  e.preventDefault()
                  if (current_page < last_page) {
                    onPageChange(current_page + 1)
                  }
                }}
              >
                <i className="mdi mdi-chevron-right"></i>
              </Link>
            </li>
          </ul>
        </div>
      </Col>
    </Row>
  )
}

// Alternative Pagination Component with First/Last buttons
const AdvancedTablePagination = ({
  paginationInfo,
  onPageChange,
  paginationWrapper = "dataTables_paginate paging_simple_numbers pagination-rounded",
  pagination = "pagination",
  showFirstLast = true,
}) => {
  if (!paginationInfo || paginationInfo.last_page <= 1) return null

  const { current_page, last_page, from, to, total } = paginationInfo

  const generatePageNumbers = () => {
    const delta = 1
    const pages = []
    const left = current_page - delta
    const right = current_page + delta

    for (let i = 1; i <= last_page; i++) {
      if (
        i === 1 ||
        i === last_page ||
        (i >= left && i <= right) ||
        (current_page <= 3 && i <= 4) ||
        (current_page >= last_page - 2 && i >= last_page - 3)
      ) {
        pages.push(i)
      } else if (pages[pages.length - 1] !== "...") {
        pages.push("...")
      }
    }

    return pages
  }

  const pageNumbers = generatePageNumbers()

  return (
    <Row className="align-items-center mt-3">
      <Col sm={12} md={5}>
        <div className="dataTables_info">
          Showing {from} to {to} of {total} Results
        </div>
      </Col>
      <Col sm={12} md={7}>
        <div className={paginationWrapper}>
          <ul className={pagination}>
            {/* First Button */}
            {showFirstLast && (
              <li
                className={`paginate_button page-item first ${
                  current_page <= 1 ? "disabled" : ""
                }`}
              >
                <Link
                  to="#"
                  className="page-link"
                  onClick={e => {
                    e.preventDefault()
                    onPageChange(1)
                  }}
                >
                  <i className="mdi mdi-chevron-double-left"></i>
                </Link>
              </li>
            )}

            {/* Previous Button */}
            <li
              className={`paginate_button page-item previous ${
                current_page <= 1 ? "disabled" : ""
              }`}
            >
              <Link
                to="#"
                className="page-link"
                onClick={e => {
                  e.preventDefault()
                  if (current_page > 1) {
                    onPageChange(current_page - 1)
                  }
                }}
              >
                <i className="mdi mdi-chevron-left"></i>
              </Link>
            </li>

            {/* Page Number Buttons */}
            {pageNumbers.map((page, index) => (
              <li
                key={index}
                className={`paginate_button page-item ${
                  page === current_page ? "active" : ""
                } ${page === "..." ? "disabled" : ""}`}
              >
                {page === "..." ? (
                  <span className="page-link">...</span>
                ) : (
                  <Link
                    to="#"
                    className="page-link"
                    onClick={e => {
                      e.preventDefault()
                      onPageChange(page)
                    }}
                  >
                    {page}
                  </Link>
                )}
              </li>
            ))}

            {/* Next Button */}
            <li
              className={`paginate_button page-item next ${
                current_page >= last_page ? "disabled" : ""
              }`}
            >
              <Link
                to="#"
                className="page-link"
                onClick={e => {
                  e.preventDefault()
                  if (current_page < last_page) {
                    onPageChange(current_page + 1)
                  }
                }}
              >
                <i className="mdi mdi-chevron-right"></i>
              </Link>
            </li>

            {/* Last Button */}
            {showFirstLast && (
              <li
                className={`paginate_button page-item last ${
                  current_page >= last_page ? "disabled" : ""
                }`}
              >
                <Link
                  to="#"
                  className="page-link"
                  onClick={e => {
                    e.preventDefault()
                    onPageChange(last_page)
                  }}
                >
                  <i className="mdi mdi-chevron-double-right"></i>
                </Link>
              </li>
            )}
          </ul>
        </div>
      </Col>
    </Row>
  )
}

// Main Table Container Component
const TableContainer = ({
  columns,
  data,
  isBordered,
  isPagination,
  theadClass,
  divClassName,
  paginationInfo,
  onPageChange,
  loading = false,
  tableClass = "align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline mt-4 border-top",
  paginationType = "advanced", // 'standard' or 'advanced'
  getRowKey,
  rowClassName,
  rowProps,
}) => {
  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
  })

  const { getHeaderGroups, getRowModel } = table

  return !loading ? (
    <React.Fragment>
      <div className={divClassName || "table-responsive"}>
        <Table hover className={tableClass} bordered={isBordered}>
          <thead className={theadClass}>
            {getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <th
                    key={header.id}
                    colSpan={header.colSpan}
                    style={header.column.columnDef.meta?.style}
                    className={header.column.columnDef.meta?.className}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {getRowModel().rows.length > 0 ? (
              getRowModel().rows.map(row => {
                const key = getRowKey ? getRowKey(row.original) : row.id
                const extraClass =
                  typeof rowClassName === "function"
                    ? rowClassName(row.original)
                    : rowClassName || ""
                const extraProps =
                  typeof rowProps === "function"
                    ? rowProps(row.original)
                    : {}
                return (
                  <tr key={key} className={extraClass} {...extraProps}>
                    {row.getVisibleCells().map(cell => (
                      <td
                        key={cell.id}
                        style={cell.column.columnDef.meta?.style}
                        className={cell.column.columnDef.meta?.className}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                )
              })
            ) : (
              <tr>
                <td colSpan={columns.length} className="text-center">
                  No data available
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {isPagination && paginationInfo && paginationInfo.total > 0 && (
        <>
          {paginationType === "advanced" ? (
            <AdvancedTablePagination
              paginationInfo={paginationInfo}
              onPageChange={onPageChange}
            />
          ) : (
            <TablePagination
              paginationInfo={paginationInfo}
              onPageChange={onPageChange}
            />
          )}
        </>
      )}
    </React.Fragment>
  ) : (
    <div className="mt-4">
      <span
        className="placeholder w-100"
        style={{ height: 45, backgroundColor: "#eff2f7" }}
      ></span>
      {Array.from({ length: 10 }).map((_, index) => (
        <span
          className="placeholder w-100 mt-2"
          style={{ height: 35, backgroundColor: "#eff2f7" }}
          key={index}
        ></span>
      ))}
    </div>
  )
}

export default TableContainer
export { TablePagination, AdvancedTablePagination }